import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { ButtonModule } from 'primeng/button'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { EcoScoreService } from 'src/app/shared/services/eco-score.service';
import { EcoscoreComponent } from 'src/app/travel-board/components/ecoscore/ecoscore.component';
import { ChartDoughnutComponent } from '../../chart-doughnut/chart-doughnut.component';
import { ImpactService } from '../../../services/impact.service';
import { ColorsService } from '../../../services/colors.service';
import { ChartData, ImpactCategory, LegendItem } from '../../../models';
import { translateElementType } from '../../../utils/element-type-translation';
import { ReductionTipsService } from 'src/app/impact/services/reduction-tips.service';
import { simulateCommitments } from '../../../utils/reduction-tip-savings';
import { COMMITMENT_STATUS_STYLE } from '../../../constants/commitment-status-style';

/**
 * Non-transport types produced by `getOverviewDetailedBreakdown()`.
 * Everything else it emits is a transport category (plane, car, train…).
 */
const CATEGORY_BY_OVERVIEW_TYPE: Record<string, ImpactCategory> = {
  accommodationImpact: 'accommodation',
  activityImpact: 'activity',
  foodImpact: 'food',
  wasteImpact: 'waste',
};

/** The side panel has a fixed width; the bar only has room for a glyph. */
const CHART_DIMENSIONS = {
  side: { size: 170, thickness: 26 },
  bar: { size: 56, thickness: 9 },
} as const;

/**
 * What the ticked tips would do to the trip, kept in view while the user
 * scrolls through them: the donut with the avoided share greyed out, the
 * totals, the eco-score, the objectives and the commitments before and
 * after — and the button
 * that writes it all to the trip.
 *
 * Rendered as a column beside the report on wide screens and as a bar stuck to
 * the bottom of the page below that; both read the same simulation.
 */
@Component({
  selector: 'app-reduction-simulation-panel',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    TranslocoModule,
    ButtonModule,
    LucideAngularModule,
    ChartDoughnutComponent,
    EcoscoreComponent,
  ],
  templateUrl: './reduction-simulation-panel.component.html',
})
export class ReductionSimulationPanelComponent {
  layout = input<'side' | 'bar'>('side');
  /** "See the tips": the page scrolls to them. */
  reveal = output<void>();

  private impactService = inject(ImpactService);
  private colorsService = inject(ColorsService);
  private translocoService = inject(TranslocoService);
  private ecoScoreService = inject(EcoScoreService);
  protected tipsService = inject(ReductionTipsService);
  protected formatCo2e = inject(FormatCo2eService);

  simulation = this.tipsService.simulation;
  hasSelection = this.tipsService.hasSelection;
  selectedCount = computed(() => this.tipsService.selectedTipIds().size);

  chart = computed(() => CHART_DIMENSIONS[this.layout()]);

  readonly statusStyle = COMMITMENT_STATUS_STYLE;

  /** Objectives and commitments read as a count, the detail on demand. */
  objectivesOpen = signal(false);
  commitmentsOpen = signal(false);

  /** Side layout only: folded to a tab, so it stops covering the report. */
  collapsed = signal(false);

  /** Trip breakdown as it stands today, coloured like the page's main donut. */
  private baseLegend = computed<LegendItem[]>(() => {
    const overview = this.impactService.overview();
    if (!overview) return [];

    const { detail, totalImpact } = overview;
    const colors = this.colorsService.generateCategoryGroupedColors(
      detail.map((item) => ({
        category: CATEGORY_BY_OVERVIEW_TYPE[item.type] ?? 'transport',
        impact: item.impact,
        type: item.type,
      })),
    );

    return detail.map((item, index) => ({
      label: translateElementType(this.translocoService, item.type),
      type: item.type,
      impact: item.impact,
      color: colors[index],
      percentage:
        totalImpact > 0 ? Math.round((item.impact / totalImpact) * 100) : 0,
    }));
  });

  /**
   * Reduced categories, then the avoided share as a grey slice. Everything is
   * simulated on trip totals; only what reaches the template follows the view
   * mode.
   */
  chartData = computed<ChartData[]>(() => {
    const simulation = this.simulation();
    if (!simulation) return [];

    const slices: ChartData[] = this.baseLegend().map((item) => ({
      label: item.label,
      value: Math.round(
        this.tipsService.toDisplayed(
          item.impact - (simulation.avoidedByType[item.type] ?? 0),
        ),
      ),
      color: item.color,
    }));
    if (simulation.avoided > 0) {
      slices.push({
        label: this.translocoService.translate(
          'reductionTips.simulation.avoided',
        ),
        value: Math.round(this.tipsService.toDisplayed(simulation.avoided)),
        color: this.colorsService.EMPTY_COLOR,
      });
    }
    return slices.filter((slice) => slice.value > 0);
  });

  displayBaseTotal = computed(() =>
    this.tipsService.toDisplayed(this.simulation()?.baseTotal ?? 0),
  );
  displayNewTotal = computed(() =>
    this.tipsService.toDisplayed(this.simulation()?.newTotal ?? 0),
  );
  displayAvoided = computed(() =>
    this.tipsService.toDisplayed(this.simulation()?.avoided ?? 0),
  );
  /** "Up to" what every tip together would avoid, before anything is ticked. */
  displayPotential = computed(() =>
    this.tipsService.toDisplayed(this.tipsService.potential()?.avoided ?? 0),
  );

  centerValue = computed(() =>
    this.formatCo2e.formatValue(this.displayNewTotal()),
  );
  centerLabel = computed(() =>
    this.formatCo2e.getUnit(this.displayNewTotal(), {
      perPerson: this.tipsService.perPerson(),
    }),
  );

  /**
   * Eco-score before and after. The carbon intensity is the total spread over
   * days and travellers, so it scales with the simulated total.
   */
  ecoScores = computed(() => {
    const simulation = this.simulation();
    const intensity = this.impactService.trip()?.tripImpact?.carbonIntensity;
    if (!simulation || intensity === undefined) return null;

    const ratio =
      simulation.baseTotal > 0 ? simulation.newTotal / simulation.baseTotal : 1;
    return {
      before: this.ecoScoreService.getEcoScore(intensity),
      after: this.ecoScoreService.getEcoScore(intensity * ratio),
    };
  });

  /**
   * Each objective without and with the ticked tips, read as the eco-score
   * is: "4,87 t → 4,82 t". Which objectives the trip can be measured on
   * depends on the trip, never on its total, so both lists line up.
   */
  objectiveRows = computed(() => {
    const total = this.tipsService.tripTotal();
    const simulation = this.simulation();
    if (total === null || !simulation) return [];

    const after = this.tipsService.compareToObjectives(
      total - simulation.avoided,
    );
    return this.tipsService.compareToObjectives(total).map((before, i) => ({
      type: before.type,
      targetLabelKey: before.targetLabelKey,
      target: before.target,
      before: before.value,
      wasReached: !before.isAboveTarget,
      after: after[i].value,
      isReached: !after[i].isAboveTarget,
    }));
  });

  /** Objectives reached, without and with the ticked tips. */
  objectiveCount = computed(() => {
    const rows = this.objectiveRows();
    return {
      before: rows.filter((row) => row.wasReached).length,
      after: rows.filter((row) => row.isReached).length,
      total: rows.length,
    };
  });

  /** Each commitment without and with the ticked tips. */
  commitmentRows = computed(() =>
    simulateCommitments(
      this.tipsService.commitments(),
      this.tipsService.tips(),
      this.tipsService.selectedTipIds(),
    ),
  );

  /** Commitments respected, out of those that apply to the trip. */
  commitmentCount = computed(() => {
    const rows = this.commitmentRows().filter(
      (row) => row.before !== 'not_applicable',
    );
    return {
      before: rows.filter((row) => row.before === 'respected').length,
      after: rows.filter((row) => row.after === 'respected').length,
      total: rows.length,
    };
  });

  hasGoals = computed(
    () => this.objectiveRows().length > 0 || this.commitmentCount().total > 0,
  );

  /** Value and unit on the displayed basis. */
  co2e(value: number): string {
    return this.formatCo2e.format(value, {
      perPerson: this.tipsService.perPerson(),
    });
  }
}
